/**
 * LLM 节点：文本处理 / 反推提示词双模式。
 *
 * - 上游有图片（图片节点 / 图片组）：vision 模式流式反推提示词，
 *   输入框作为附加指令（可选），结果写入 output 供下游生图节点消费。
 * - 上游仅有文本：作为「文本处理器」，输入框为处理指令（可选）。
 * - 无上游：作为「文本生成器」，输入框为主输入。
 *
 * 节点不做任何编排（建下游节点/模式切换已移除）：行为完全由输入与
 * 上游形态驱动；编排职责归属智能体节点与拖线创建。
 */
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { Sparkles, Copy, Pencil, Check, X } from 'lucide-react';
import type { CanvasNode, LlmNodeData } from '../types';
import { NodeWrapper } from './NodeWrapper';
import { NodeToolbarButton } from './NodeToolbar';
import { boardActions, getBoardState, useBoard } from '../state/boardStore';
import {
  aggregateUpstreamText,
  aggregateUpstreamReferenceImages,
  buildUpstreamSignature,
  collectUpstreamImageAssetGroups,
  waitForUpstreamIdle,
} from '../state/upstream';
import { resolveAtReferences } from '../atReference';
import { resolveLlmInvocation } from '../state/llmInvocation';
import { reversePromptStream, toVisionFetchableRef, checkVisionStreamEmpty } from '../services/visionApi';
import { readSSEStream } from '../sseStream';
import { platformFetch } from '../../../platform/http';
import { resolveBrowserMediaUrl } from '../../../utils/storageUrl';
import { PromptMentionEditor } from './PromptMentionEditor';
import toast from '../../../components/Toast';

interface LlmNodeProps {
  node: CanvasNode<LlmNodeData>;
  selected: boolean;
  highlighted: boolean;
}

export function LLMNode({ node, selected, highlighted }: LlmNodeProps) {
  const [draft, setDraft] = useState(node.data.input || '');
  const [streamingText, setStreamingText] = useState('');
  const [outputExpanded, setOutputExpanded] = useState(true);
  const abortRef = useRef<AbortController | null>(null);

  const generating = node.data.generating || false;
  const output = node.data.output || '';
  const errorMessage = node.data.errorMessage;

  // 输入框内容与 node.data.input 同步（撤销/重做等外部变更后刷新本地 draft）
  useEffect(() => {
    setDraft(node.data.input || '');
  }, [node.data.input]);

  // 双模态：根据上游文本决定是否可以生成。
  // 只订阅上游签名，签名不变时跳过全图遍历，避免每次击键都 aggregate
  const upstreamSig = useBoard((s) => buildUpstreamSignature(node.id, s.nodes, s.edges));
  const { nodes, edges } = useMemo(() => getBoardState(), [upstreamSig]);

  const upstreamText = useMemo(
    () => aggregateUpstreamText(node.id, nodes, edges),
    [node.id, nodes, edges]
  );
  // 上游图片资产分组（图片节点 1 张 / 图片组逐张；已展开开启透传的上游 LLM）
  const upstreamAssetGroups = useMemo(
    () => collectUpstreamImageAssetGroups(node.id, nodes, edges),
    [node.id, nodes, edges]
  );
  const upstreamImages = useMemo(
    () => aggregateUpstreamReferenceImages(node.id, nodes, edges),
    [node.id, nodes, edges]
  );
  const knownRefNos = useMemo(
    () =>
      new Set(
        nodes
          .filter((n) => n.type === 'image' || n.type === 'image_group')
          .map((n) => (n.data as Record<string, unknown>).refNo)
          .filter((v): v is number => typeof v === 'number')
      ),
    [nodes]
  );
  // @引用预览：输入含 @图N 时参考图按标记实际引用数计（与提交解析同口径）
  const atPreview = useMemo(
    () => resolveAtReferences(draft, upstreamAssetGroups, knownRefNos),
    [draft, upstreamAssetGroups, knownRefNos]
  );

  const hasUpstream = upstreamText.length > 0;
  const upstreamImageCount = upstreamImages.length;
  const effectiveImageCount = atPreview ? atPreview.referenceImages.length : upstreamImageCount;
  const isVision = effectiveImageCount > 0;
  const canGenerate = hasUpstream || isVision || draft.trim().length > 0;

  const handleGenerate = useCallback(async () => {
    if (generating) return;
    const input = draft.trim();

    // 先置生成中（等待期间也防重复点击），清空输出
    boardActions.updateNodeData(node.id, { generating: true, errorMessage: null, output: '' });
    setStreamingText('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // 上游链路执行中：等待完成后再聚合（防止聚合到流式中途等不完整输出），重读最新图状态
      await waitForUpstreamIdle(node.id);
      const { nodes: freshNodes, edges: freshEdges } = getBoardState();
      // 统一解析：上游文本 + 图片资产 + 输入框 @图N（与链路执行同口径）
      const invocation = resolveLlmInvocation(node.id, freshNodes, freshEdges, input);
      if (invocation.warnings.length > 0) toast(invocation.warnings.join('；'));

      // 双模态：有上游（文本或图片）或输入时执行，其余情况不提交
      if (!invocation.hasInput) {
        boardActions.updateNodeData(node.id, { generating: false });
        return;
      }

      let accumulated = '';
      const onDelta = (delta: string) => {
        accumulated += delta;
        setStreamingText(accumulated);
      };

      if (invocation.isVision) {
        // vision 模式：流式反推提示词；blob 引用先转 data URL，服务端按图集解析。
        // 上游文本（text/llm 输出）已并入 instruction——用户连文本的意图是
        // "结合这段文字反推"（如"侧重描述服装"），不能静默丢弃
        const images: string[] = [];
        for (const url of invocation.images) {
          images.push(await toVisionFetchableRef(url));
        }
        await reversePromptStream({
          images,
          instruction: invocation.instruction,
          onDelta,
          signal: controller.signal,
        });
        checkVisionStreamEmpty(accumulated);
      } else {
        // process 通用模式：后端按"文本 + 指令"驱动，行为由输入确定
        const body: Record<string, string> = { mode: 'process' };
        if (invocation.upstreamText) body.upstreamText = invocation.upstreamText;
        if (invocation.resolvedInput) body.instruction = invocation.resolvedInput;
        // 兼容：同时传 prompt（无上游时 = 输入，有上游时后端优先用 upstreamText）
        body.prompt = invocation.upstreamText
          ? invocation.resolvedInput || invocation.upstreamText
          : invocation.resolvedInput;

        const response = await platformFetch('/canvas/llm/stream', {
          method: 'POST',
          body: JSON.stringify(body),
          signal: controller.signal,
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new Error(data.error || '生成失败');
        }

        await readSSEStream(response, (payload) => {
          if (payload === '[DONE]') return;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) {
              throw new Error(parsed.error);
            }
            if (parsed.content) {
              onDelta(parsed.content);
            }
          } catch (err) {
            // 回调内抛出的业务错误（parsed.error）需要继续上抛；
            // JSON.parse 失败的非 JSON 行静默跳过（与 vision 路径同口径）
            if (err instanceof Error && err.message && !err.message.startsWith('Unexpected')) {
              throw err;
            }
          }
        });
      }

      // 流结束但没收到任何内容也没收到错误（网络截断 / 后端中途崩溃）
      if (!accumulated) {
        throw new Error('生成失败：未收到任何内容，请稍后重试');
      }

      boardActions.updateNodeData(node.id, { output: accumulated, generating: false });
      boardActions.commitNodeMutation();
      setStreamingText('');
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      boardActions.updateNodeData(node.id, {
        generating: false,
        errorMessage: (err as Error).message || '生成失败',
      });
      setStreamingText('');
    } finally {
      abortRef.current = null;
    }
  }, [draft, generating, node.id]);

  const handleStop = () => {
    abortRef.current?.abort();
    boardActions.updateNodeData(node.id, { generating: false });
  };

  const handleCopyOutput = () => {
    const text = output || streamingText;
    if (text) navigator.clipboard.writeText(text);
  };

  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState('');
  const editTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const handleStartEdit = () => {
    setEditDraft(output || '');
    setEditing(true);
    // 等下一帧 textarea 挂载后聚焦
    requestAnimationFrame(() => editTextareaRef.current?.focus());
  };

  const handleSaveEdit = () => {
    boardActions.updateNodeData(node.id, { output: editDraft.trim() });
    setEditing(false);
  };

  const handleCancelEdit = () => {
    setEditing(false);
  };

  const displayOutput = streamingText || output;

  return (
    <NodeWrapper
      node={node}
      selected={selected}
      highlighted={highlighted}
      stretchContent
      toolbar={
        displayOutput && !generating ? (
          <>
            <NodeToolbarButton onClick={handleStartEdit} title="编辑输出">
              <Pencil className="h-3.5 w-3.5" />
            </NodeToolbarButton>
            <NodeToolbarButton onClick={handleCopyOutput} title="复制输出">
              <Copy className="h-3.5 w-3.5" />
            </NodeToolbarButton>
          </>
        ) : displayOutput ? (
          <NodeToolbarButton onClick={handleCopyOutput} title="复制输出">
            <Copy className="h-3.5 w-3.5" />
          </NodeToolbarButton>
        ) : null
      }
    >
      <div className="flex min-h-0 w-full flex-1 flex-col gap-2">
        {/* 反推模式标识：上游有图即强制反推（唯一可执行模式），常驻显示 */}
        {isVision && (
          <div className="rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-600">
            反推模式：
            {atPreview
              ? `@引用 ${effectiveImageCount} / 可引用 ${upstreamImageCount}`
              : `已连接 ${upstreamImageCount}`}
            {' '}张图片，输出为反推提示词（输入框为可选指令）
          </div>
        )}

        {/* 输入区：支持 @图N 引用上游图片（chip 化） */}
        <PromptMentionEditor
          value={draft}
          onChange={(v) => {
            setDraft(v);
            boardActions.updateNodeData(node.id, { input: v });
          }}
          options={upstreamAssetGroups
            .filter((g): g is typeof g & { refNo: number } => typeof g.refNo === 'number')
            .map((g) => ({
              refNo: g.refNo,
              thumbnail: resolveBrowserMediaUrl(g.urls[0]) || g.urls[0],
              isGroup: g.type === 'image_group',
              count: g.urls.length,
            }))}
          placeholder={
            isVision
              ? '反推指令（可选），如：侧重人物与光影...'
              : hasUpstream
                ? '处理指令（可选），如：扩写成绘画提示词...'
                : '输入想法、文本或指令...'
          }
          className="min-h-[4rem] flex-1"
          wrapperClassName="flex min-h-[4rem] flex-1 flex-col"
        />

        {/* 图片透传：把本节点上游图片继续暴露给下游生图 / 生视频节点作参考图 */}
        {upstreamImageCount > 0 && (
          <label className="flex cursor-pointer items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1.5 text-xs text-amber-700">
            <input
              type="checkbox"
              checked={!!node.data.passthroughImages}
              onChange={() => {
                boardActions.updateNodeData(node.id, {
                  passthroughImages: !node.data.passthroughImages,
                });
                boardActions.commitNodeMutation();
              }}
              disabled={generating}
              className="mt-0.5 h-3.5 w-3.5 accent-amber-500"
            />
            <span>
              <span className="font-medium">图片透传给下游</span>
              <span className="ml-1 text-amber-600/80">
                下游生图 / 生视频可 @图N 引用本节点上游图片
              </span>
            </span>
          </label>
        )}

        {/* 生成按钮 */}
        <button
          type="button"
          onClick={generating ? handleStop : handleGenerate}
          disabled={!generating && !canGenerate}
          className={`flex w-full items-center justify-center gap-1.5 rounded-md py-2 text-sm font-medium transition ${
            generating
              ? 'bg-amber-500 text-white hover:bg-amber-600'
              : 'bg-amber-100 text-amber-700 hover:bg-amber-200 disabled:opacity-40 disabled:cursor-not-allowed'
          }`}
        >
          <Sparkles className="h-4 w-4" />
          {generating ? '生成中...' : isVision ? '反推提示词' : hasUpstream ? '处理上游文本' : '生成'}
        </button>

        {/* 错误信息 */}
        {errorMessage && (
          <div className="rounded-md bg-red-50 px-2 py-1.5 text-xs text-red-600">
            {errorMessage}
          </div>
        )}

        {/* 输出区（可折叠 / 可编辑） */}
        {displayOutput && (
          <div
            className={`rounded-md border border-amber-200 bg-amber-50 p-2 ${
              outputExpanded && !editing ? 'flex min-h-0 flex-1 flex-col' : ''
            }`}
          >
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-medium text-amber-600">输出</span>
              {editing ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={handleSaveEdit}
                    className="grid h-5 w-5 place-items-center rounded text-amber-500 transition hover:bg-amber-100"
                    title="保存"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="grid h-5 w-5 place-items-center rounded text-amber-400 transition hover:bg-amber-100"
                    title="取消"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <span
                  className="cursor-pointer text-[10px] text-amber-400"
                  onClick={() => setOutputExpanded(!outputExpanded)}
                >
                  {outputExpanded ? '收起' : '展开'}
                </span>
              )}
            </div>
            {editing ? (
              <textarea
                ref={editTextareaRef}
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleCancelEdit();
                }}
                className="min-h-[6rem] w-full resize-none rounded border border-amber-300 bg-white p-1.5 text-sm text-light-text outline-none focus:border-amber-400"
              />
            ) : (
              <div
                className={`cursor-pointer whitespace-pre-wrap break-words text-sm text-light-text ${
                  outputExpanded ? 'min-h-0 flex-1 overflow-y-auto' : 'line-clamp-2'
                }`}
                onClick={() => setOutputExpanded(!outputExpanded)}
              >
                {displayOutput}
                {streamingText && <span className="animate-pulse text-amber-500">|</span>}
              </div>
            )}
          </div>
        )}
      </div>
    </NodeWrapper>
  );
}
